import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { API_BASE_URL } from '../config/api';
import { useProfile } from "../contexts/ProfileContext";
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";

const ToolsTrends = () => {
  const [generating, setGenerating] = useState(false);
  const [analysis, setAnalysis] = useState(null); // Changed to null to represent object
  const [postsAnalyzed, setPostsAnalyzed] = useState(0);
  const [timeRange, setTimeRange] = useState('month');
  const [customRequest, setCustomRequest] = useState("");
  const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

  const profile = useProfile();

  const analyzeTrends = async () => {
    try {
		  const formData = {};
		  formData.age = profile.ageRange;
		  formData.gender = profile.genderTarget;
		  formData.country = "USA";
		  formData.niche = profile.interests;
      formData.timeRange = timeRange;
      formData.customRequest = customRequest;

      setGenerating(true);
      setAnalysis(null); // Reset to null instead of empty array
      setPostsAnalyzed(0);
      
      const counterInterval = setInterval(() => {
        const volatility = Math.random();
        const increment = volatility > 0.8 
          ? Math.floor(Math.random() * 5000 + 2000)
          : Math.floor(Math.random() * 300 + 100);  
        
        setPostsAnalyzed(prev => prev + increment);
      }, Math.floor(Math.random() * 200 + 50));

      const response = await axiosClient.post("https://api.viralgpt.com/tools/trends", formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      clearInterval(counterInterval);

      if (response.data && response.data.analysis) {
        // Parse the analysis if it's a string, otherwise use directly if it's an object
        const trendData = typeof response.data.analysis === 'string' 
          ? JSON.parse(response.data.analysis)
          : response.data.analysis;

        // Validate the structure
        if (!trendData.trends || !trendData.audio || !trendData.engagement || 
            !trendData.themes || !trendData.technical) {
          throw new Error("Invalid trends data structure");
        }

        setAnalysis(trendData);
        setFunctionSuccess("Trends analyzed successfully.");
      } else {
        throw new Error("Invalid response format from server");
      }
      
    } catch (error) {
      console.error("Trend analysis error:", error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.details ||
                          error.message || 
                          "An unexpected error occurred";
      setFunctionError("An error occurred while analyzing trends: " + errorMessage);
    } finally {
      setGenerating(false);
      setPostsAnalyzed(0);
    }
  };

  return (
    <div className="bg-white border p-6">
      <h5>Trends Analyzer</h5>
      <p className="text-muted">Get real-time insights about current Instagram/TikTok trends and viral content strategies relevant to you.</p>

      <Alert 
        variant="danger" 
        message={functionError} 
        show={!!functionError} 
        onClose={() => setFunctionError("")} 
      />
      
      <Alert 
        variant="success" 
        message={functionSuccess} 
        show={!!functionSuccess} 
        onClose={() => setFunctionSuccess("")} 
      />

      <Form.Group controlId="timeRange" className="mb-3">
        <Form.Select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="mb-3"
        >
          <option value="week">Past Week</option>
          <option value="month">Past Month</option>
          <option value="year">Past Year</option>
        </Form.Select>
      </Form.Group>

      <Form.Group controlId="customRequest" className="mb-3">
        <Form.Label>Custom Focus (Optional)</Form.Label>
        <Form.Control
          type="text"
          placeholder="E.g. Dance trends, beauty products, Drake, etc."
          value={customRequest}
          onChange={(e) => setCustomRequest(e.target.value)}
        />
      </Form.Group>

      <div className="d-flex align-items-center">
        <Button 
          className={generating ? "disabled bg-instagram" : "bg-instagram"} 
          onClick={analyzeTrends}
          disabled={generating}
        >
          {generating 
            ? `Analyzing... (${postsAnalyzed.toLocaleString()} posts processed)` 
            : "Analyze Current Trends"}
        </Button>
        <div 
          style={{fontSize: "1.5rem"}} 
          className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!generating ? "d-none" : ""}`}
        />
      </div>

      {analysis && (
        <div className="mt-4">
          {Object.entries({
            "Trending Content": analysis.trends,
            "Popular Audio": analysis.audio,
            "Engagement Tactics": analysis.engagement,
            "Content Themes": analysis.themes,
            "Technical Strategies": analysis.technical
          }).map(([title, items], index) => (
            <div key={index} className="mb-4">
              <h6 className="text-instagram mb-2">{title}</h6>
              <ul className="list-unstyled m-0">
                {items.map((item, itemIndex) => (
                  <li key={itemIndex} className="d-flex align-items-start mb-2">
                    <i className="fa fa-chart-line text-instagram mt-1 me-3" />
                    <span className="flex-grow-1">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolsTrends;